import { For, Show, createSignal, onCleanup, onMount } from "solid-js";
import { Portal } from "solid-js/web";

export type ToastTone = "success" | "error" | "info" | "warning";

export interface Toast {
    id: string;
    message: string;
    tone: ToastTone;
    durationMs: number;
}

const [toasts, setToasts] = createSignal<Toast[]>([]);

let counter = 0;

function push(tone: ToastTone, message: string, durationMs = 4000): string {
    counter += 1;
    const id = `t-${counter}-${Date.now()}`;
    setToasts((prev) => [...prev, { id, message, tone, durationMs }]);
    if (durationMs > 0) {
        setTimeout(() => dismiss(id), durationMs);
    }
    return id;
}

function dismiss(id: string): void {
    setToasts((prev) => prev.filter((t) => t.id !== id));
}

/**
 * API global de toasts -- substitui window.alert pra sucesso/erro/info.
 * Uso: `toast.error("falhou", "detalhes opcionais")` ou `toast.success("salvo")`.
 *
 * Mount o `<ToastViewport />` uma vez no root.
 */
export const toast = {
    success: (msg: string, durationMs?: number) => push("success", msg, durationMs),
    error: (msg: string, durationMs?: number) => push("error", msg, durationMs ?? 6000),
    info: (msg: string, durationMs?: number) => push("info", msg, durationMs),
    warning: (msg: string, durationMs?: number) => push("warning", msg, durationMs),
    dismiss,
};

export function ToastViewport() {
    return (
        <Portal>
            <div class="ui-toast-viewport" role="region" aria-live="polite" aria-label="Notificações">
                <For each={toasts()}>{(t) => <ToastCard toast={t} />}</For>
            </div>
        </Portal>
    );
}

function ToastCard(props: { toast: Toast }) {
    const [exiting, setExiting] = createSignal(false);
    let timeoutId: number | undefined;

    onMount(() => {
        if (props.toast.durationMs > 0) {
            timeoutId = window.setTimeout(() => setExiting(true), props.toast.durationMs - 200);
        }
    });

    onCleanup(() => {
        if (timeoutId) clearTimeout(timeoutId);
    });

    return (
        <div class={`ui-toast tone-${props.toast.tone} ${exiting() ? "is-exiting" : ""}`} role="status">
            <span class="ui-toast-icon" aria-hidden="true">
                {iconFor(props.toast.tone)}
            </span>
            <span class="ui-toast-msg">{props.toast.message}</span>
            <Show when={props.toast.durationMs > 0}>
                <button
                    type="button"
                    class="ui-toast-close"
                    aria-label="Fechar notificação"
                    onClick={() => dismiss(props.toast.id)}
                >
                    {"×"}
                </button>
            </Show>
        </div>
    );
}

function iconFor(tone: ToastTone): string {
    switch (tone) {
        case "success":
            return "OK";
        case "error":
            return "!";
        case "warning":
            return "!";
        case "info":
            return "i";
    }
}
